import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useCompanyFormats from '../../../../../../utilities/useCompanyFormats';
import useFinanceProductOptions from '../../../shared/useFinanceProductOptions';
import useSelectedFinderVehicles from '../../../shared/useSelectedFinderVehicles';
import useSelectedVariants from '../../../shared/useSelectedVariants';
import { type FinanceProductFormValues } from '../../index';
import useTerms from '../LeaseReferenceTable/useTerms';
import { type LeasePurchaseTableRecord } from './shared';
import useAverageMileages from './useAverageMileages';
import useColumns from './useColumns';
import useRecords from './useRecords';
import useVehicleReferenceParameterRecords from './useVehicleReferenceParameterRecords';

const useLeasePurchaseReferenceData = ({ isDisabled = false }: { isDisabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { formatAmount } = useCompanyFormats();

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const { averageMileageUnitOption } = useFinanceProductOptions();
    const mileageUnit = values.residualValue.averageMileage.defaultUnit;
    const mileageUnitLabel = averageMileageUnitOption.find(mileageUnitOpt =>
        mileageUnitOpt.value.match(mileageUnit)
    ).label;

    const {
        variantSuiteIds,
        residualValue: residualValueSetting,
        term: termSettings,
        vehicleReferenceParameters,
    } = values;

    const terms = useTerms(termSettings);
    const averageMileages = useAverageMileages(residualValueSetting.averageMileage);

    const variants = useSelectedVariants(variantSuiteIds);
    const finderVehicles = useSelectedFinderVehicles(variantSuiteIds);

    const variantRecords = useRecords(
        variants,
        residualValueSetting,
        terms,
        averageMileages,
        formatAmount,
        mileageUnitLabel
    );
    const finderVehicleRecords = useRecords(
        finderVehicles,
        residualValueSetting,
        terms,
        averageMileages,
        formatAmount,
        mileageUnitLabel
    );

    const vehicleReferenceParameterRecords = useVehicleReferenceParameterRecords(
        vehicleReferenceParameters,
        residualValueSetting,
        terms,
        averageMileages,
        formatAmount,
        mileageUnitLabel
    );

    const onUpdate = useCallback(
        (record: LeasePurchaseTableRecord) => {
            // clone current records
            const current = [
                ...variantRecords,
                ...finderVehicleRecords,
                ...vehicleReferenceParameterRecords.variant,
                ...vehicleReferenceParameterRecords.finderVehicle,
            ];

            // find target index
            const { variantSuiteId: targetId, mileage: targetMileage } = record;
            const index = current.findIndex(
                ({ variantSuiteId, mileage }) => variantSuiteId === targetId && mileage === targetMileage
            );

            // update target
            current.splice(index, 1, record);

            const updated = current
                .flatMap(
                    (
                        record // map terms to table
                    ) =>
                        terms.map(term => ({
                            variantSuiteId: record.variantSuiteId,
                            mileage: record.mileage,
                            term,
                            value: record[`term${term}`],
                        }))
                )
                .filter(({ value }) => Number.isFinite(value));

            // then update formik
            setFieldValue('residualValue.table', updated);
        },
        [
            finderVehicleRecords,
            setFieldValue,
            terms,
            variantRecords,
            vehicleReferenceParameterRecords.finderVehicle,
            vehicleReferenceParameterRecords.variant,
        ]
    );

    const variantColumns = useColumns(
        t('financeProductDetails:tables.leasePurchase.columnTitles.variantTerm'),
        terms,
        variants,
        isDisabled,
        onUpdate,
        residualValueSetting.residualValueUnit
    );
    const finderVehicleColumns = useColumns(
        t('financeProductDetails:tables.leasePurchase.columnTitles.finderVehicleTerm'),
        terms,
        finderVehicles,
        isDisabled,
        onUpdate,
        residualValueSetting.residualValueUnit
    );

    return useMemo(
        () => ({
            variantColumns,
            variantRecords: [...variantRecords, ...vehicleReferenceParameterRecords.variant],
            finderVehicleColumns,
            finderVehicleRecords: [...finderVehicleRecords, ...vehicleReferenceParameterRecords.finderVehicle],
        }),
        [finderVehicleColumns, finderVehicleRecords, variantColumns, variantRecords, vehicleReferenceParameterRecords]
    );
};

export default useLeasePurchaseReferenceData;
